// Iter 33d Follow-up 9 — Right-side validation drawer (replaces the
// below-canvas full panel). Slides in from the right without an overlay
// so the canvas stays visible and interactive — validation reads as
// guided support, not a second page underneath the schedule.
//
// Violations are grouped by `kind` into collapsible sections (one open
// at a time). Each row carries the same plain-language reason, "Show
// on canvas" jump, and constraint-validated "Find alternative" suggester
// from earlier iterations.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Warning,
  CheckCircle,
  CaretRight,
  CaretDown,
  ArrowsClockwise,
  X,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { DAYS, fmtTime } from "./utils";

// Plain-language section labels (shown as the group header in the drawer)
const KIND_LABELS = {
  provider_unavailable_day: "Provider day off",
  provider_blocked_time: "Provider blocked time",
  provider_partial_window: "Outside provider window",
  flex_day_outside_window: "Flex-day window",
  flex_day_closed: "School closed (flex day)",
  grade_constraint: "Grade rule overlaps",
  attendance_window: "Outside attendance window",
  teacher_constraint: "Teacher rule overlaps",
  student_constraint: "Student rule overlaps",
  cross_provider: "Cross-provider conflicts",
  archived_student_block: "Archived / graduated students",
  service_obligation_no_blocks: "Service commitments without blocks",
  service_obligation_under_minutes: "Under-scheduled commitments",
  service_obligation_over_minutes: "Over-scheduled commitments",
};

const KIND_TONE = {
  cross_provider: "high",
  provider_blocked_time: "high",
  provider_unavailable_day: "high",
  archived_student_block: "high",
  attendance_window: "med",
  grade_constraint: "med",
  teacher_constraint: "med",
  student_constraint: "med",
  flex_day_closed: "med",
  flex_day_outside_window: "med",
  provider_partial_window: "med",
  service_obligation_no_blocks: "med",
  service_obligation_under_minutes: "low",
  service_obligation_over_minutes: "low",
};

// Stable display order — high tones first, commitments last.
const KIND_ORDER = [
  "cross_provider",
  "provider_blocked_time",
  "provider_unavailable_day",
  "archived_student_block",
  "attendance_window",
  "grade_constraint",
  "teacher_constraint",
  "student_constraint",
  "flex_day_closed",
  "flex_day_outside_window",
  "provider_partial_window",
  "service_obligation_no_blocks",
  "service_obligation_under_minutes",
  "service_obligation_over_minutes",
];

const TONE_DOT = {
  high: "bg-[#B85C5C]",
  med: "bg-[#D9A05B]",
  low: "bg-[#9B9890]",
};

export default function BuilderValidationDrawer({
  open,
  onOpenChange,
  loading,
  violations,
  lastRunAt,
  mySessions,
  weekDates,
  providerId,
  schedulePeriodId,
  onJumpToBlock,
  onMoveBlock,
  onRemoveBlock,
  // FP3-065 follow-up #2 enhancement (Jun 1, 2026) — Manual
  // re-validate trigger. The validator auto-refreshes whenever the
  // session signature flips, but operators occasionally want a
  // belt-and-braces "confirm it's actually fine" lever — e.g. after
  // closing the wizard chooser without applying, or after a soft
  // refetch they're not sure landed.
  onRecheck,
}) {
  const [openKind, setOpenKind] = useState(null); // accordion: only one kind open at a time
  const [suggestionsByBlock, setSuggestionsByBlock] = useState({});
  const drawerRef = useRef(null);

  // Group violations by kind, preserving stable ordering.
  const groups = useMemo(() => {
    const byKind = new Map();
    violations.forEach((v) => {
      if (!byKind.has(v.kind)) byKind.set(v.kind, []);
      byKind.get(v.kind).push(v);
    });
    return KIND_ORDER
      .filter((k) => byKind.has(k))
      .concat(Array.from(byKind.keys()).filter((k) => !KIND_ORDER.includes(k)))
      .map((kind) => ({ kind, items: byKind.get(kind) || [] }));
  }, [violations]);

  // Auto-expand the first non-empty group whenever a fresh run lands so the
  // user lands on the most-pressing issues first. Reset on re-runs.
  useEffect(() => {
    if (!lastRunAt) return;
    if (groups.length === 0) {
      setOpenKind(null);
      return;
    }
    setOpenKind((prev) =>
      prev && groups.some((g) => g.kind === prev) ? prev : groups[0].kind
    );
  }, [lastRunAt, groups]);

  // Map block_id → first matching session for display metadata
  const blockMeta = useMemo(() => {
    const m = new Map();
    mySessions.forEach((s) => {
      const bid = s._recurring_block_id;
      if (bid && !m.has(bid)) m.set(bid, s);
    });
    return m;
  }, [mySessions]);

  const fetchSuggestionsFor = async (blockId) => {
    if (suggestionsByBlock[blockId]?.suggestions) return;
    setSuggestionsByBlock((prev) => ({
      ...prev,
      [blockId]: { loading: true, suggestions: null, error: null, expanded: true },
    }));
    try {
      const r = await api.suggestAlternativeSlots({
        provider_id: providerId,
        block_id: blockId,
        schedule_period_id: schedulePeriodId,
        max_results: 3,
      });
      setSuggestionsByBlock((prev) => ({
        ...prev,
        [blockId]: {
          loading: false,
          suggestions: r.data?.suggestions || [],
          // Iter 33d Follow-up 11 — Surface the suggester's `reason`
          // (e.g. "provider_off_this_day") so the panel can show a
          // helpful explanation instead of a generic "no slots fit".
          reason: r.data?.reason || null,
          // Iter 33d Follow-up 14e — Backend marks `cross_day=true` when
          // it returned suggestions on OTHER weekdays because same-day
          // was impossible. Surfaced as a small notice above the list.
          cross_day: !!r.data?.cross_day,
          error: null,
          expanded: true,
        },
      }));
    } catch (e) {
      setSuggestionsByBlock((prev) => ({
        ...prev,
        [blockId]: {
          loading: false,
          suggestions: [],
          error: e.message || "Failed to load suggestions",
          expanded: true,
        },
      }));
    }
  };

  const toggleAlternatives = (blockId) => {
    const cur = suggestionsByBlock[blockId];
    if (cur && cur.expanded) {
      setSuggestionsByBlock((prev) => ({
        ...prev,
        [blockId]: { ...cur, expanded: false },
      }));
    } else if (cur) {
      setSuggestionsByBlock((prev) => ({
        ...prev,
        [blockId]: { ...cur, expanded: true },
      }));
    } else {
      fetchSuggestionsFor(blockId);
    }
  };

  // Don't render until we have a result to show — avoids drawer DOM at all
  // before the first save.
  if (!lastRunAt && !loading) return null;

  const totalIssues = violations.length;

  return (
    <>
      {/* Iter 40 — Soft layered visual treatment. Subtle overlay dims the
          canvas behind to indicate the drawer is the active surface
          without becoming modal — the canvas is still clickable through
          the dim layer (pointer-events: none). The drawer itself uses a
          slightly off-white panel with a wider shadow + thin left rule
          for visual separation. */}
      <div
        aria-hidden
        className={`fixed inset-0 z-20 transition-opacity duration-300 pointer-events-none ${
          open ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "linear-gradient(to left, rgba(15,23,42,0.06) 380px, transparent 100%)",
        }}
      />
      {/* Slide-in drawer. NOT modal — no backdrop. Canvas stays usable
          behind it. */}
      <aside
        ref={drawerRef}
        data-testid="builder-validation-drawer"
        aria-hidden={!open}
        className={`fixed top-0 right-0 h-screen w-[400px] max-w-[88vw] bg-[#FAFAF7] border-l border-[#D4D2CC] shadow-[-12px_0_32px_-8px_rgba(15,23,42,0.12)] z-30 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header — slightly inset, tonal contrast against the drawer body */}
        <header className="px-5 py-3.5 bg-white border-b border-[#E5E3DB] flex items-center gap-2.5 shrink-0">
          {loading ? (
            <ArrowsClockwise size={16} className="text-[#6B7280] animate-spin" />
          ) : totalIssues === 0 ? (
            <CheckCircle size={16} weight="fill" className="text-[#1E3F33]" />
          ) : (
            <Warning size={16} weight="fill" className="text-[#D9A05B]" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#1F2937] heading-font leading-tight">
              {loading
                ? "Validating…"
                : totalIssues === 0
                ? "No conflicts detected"
                : `${totalIssues} issue${totalIssues !== 1 ? "s" : ""} to review`}
            </p>
            {!loading && totalIssues > 0 && (
              <p className="text-[10px] text-[#6B7280] truncate">
                Grouped by type · click a row to jump to the canvas
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onRecheck}
            disabled={loading || !onRecheck}
            data-testid="val-drawer-recheck"
            className="p-1.5 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#ECEAE4] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#6B7280]"
            title="Re-check schedule"
            aria-label="Re-check schedule"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="val-drawer-close"
            className="p-1.5 rounded-md text-[#6B7280] hover:text-[#1F2937] hover:bg-[#ECEAE4]"
            title="Close"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {!loading && totalIssues === 0 && (
            <div className="px-4 py-6 text-[12px] text-[#6B7280]">
              Your schedule passes all checked rules. Re-Save any time to
              re-validate.
            </div>
          )}
          {groups.map(({ kind, items }) => {
            const isOpen = openKind === kind;
            const tone = KIND_TONE[kind] || "low";
            return (
              <div key={kind} data-testid={`val-group-${kind}`} className="border-b border-[#ECEAE4]">
                <button
                  type="button"
                  data-testid={`val-group-toggle-${kind}`}
                  onClick={() => setOpenKind(isOpen ? null : kind)}
                  className={`w-full px-4 py-2.5 flex items-center gap-2 text-left transition-colors ${
                    isOpen ? "bg-[#F9F8F6]" : "hover:bg-[#FCFBF9]"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TONE_DOT[tone]}`} />
                  <span className="text-[12px] font-semibold text-[#1F2937] flex-1 truncate">
                    {KIND_LABELS[kind] || kind}
                  </span>
                  <span className="text-[10px] tabular-nums text-[#6B7280] bg-[#F3F1EC] rounded px-1.5 py-0.5">
                    {items.length}
                  </span>
                  {isOpen ? (
                    <CaretDown size={11} className="text-[#9B9890]" />
                  ) : (
                    <CaretRight size={11} className="text-[#9B9890]" />
                  )}
                </button>
                {isOpen && (
                  <div className="bg-white">
                    {items.map((v, i) => {
                      const sess = v.block_id ? blockMeta.get(v.block_id) : null;
                      const dayIdx = sess ? (weekDates || []).indexOf(sess.date) : -1;
                      const dayLabel = dayIdx >= 0 ? DAYS[dayIdx] : null;
                      const hasBlock = !!v.block_id;
                      const studentName =
                        v.student_name ||
                        (sess?.students?.[0]
                          ? `${sess.students[0].first_name} ${sess.students[0].last_name}`
                          : sess?.personal_label || "Block");
                      const cache = hasBlock ? suggestionsByBlock[v.block_id] : null;
                      const showAlts = cache?.expanded;
                      return (
                        <div
                          key={`${v.block_id || "x"}-${i}`}
                          data-testid={`val-row-${v.block_id || `commitment-${i}`}`}
                          className="px-4 py-2.5 border-t border-[#F3F1EC] first:border-t-0"
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-[#1F2937] leading-tight">
                                {studentName}
                              </p>
                              {(dayLabel || v.start_time) && (
                                <p className="text-[10px] text-[#6B7280] tabular-nums mt-0.5">
                                  {dayLabel ? dayLabel + " · " : ""}
                                  {sess && hasBlock
                                    ? `${fmtTime(sess.start_time)}–${fmtTime(sess.end_time)}`
                                    : v.start_time
                                    ? `${fmtTime(v.start_time)}–${fmtTime(v.end_time)}`
                                    : ""}
                                </p>
                              )}
                              <p className="text-[11px] text-[#4B5563] mt-1">{v.details}</p>
                              {/* FP3-065 follow-up #3 — Surface the
                                  math breakdown for over/under
                                  commitments so operators can see
                                  exactly how the validator counted
                                  each block. Hidden when the
                                  breakdown is empty (no scheduled
                                  blocks) or when the violation isn't
                                  a coverage one. */}
                              {v.math_detail && Array.isArray(v.math_detail.blocks)
                                && v.math_detail.blocks.length > 0 && (
                                <div
                                  data-testid={`val-math-detail-${v.kind}-${v.student_id || ""}`}
                                  className="text-[10.5px] text-[#6B7280] mt-1 leading-snug"
                                >
                                  <span className="text-[9.5px] uppercase tracking-[0.06em] font-semibold mr-1">
                                    Counted as
                                  </span>
                                  {v.math_detail.blocks.map((b, i) => {
                                    const mins = b.duration_min || 0;
                                    const occ = b.occurrences_per_period || 0;
                                    const wom = b.weeks_of_month;
                                    const womLabel = Array.isArray(wom) && wom.length > 0
                                      ? ` (weeks ${wom.join(",")})`
                                      : "";
                                    return (
                                      <span key={b.block_id || i}>
                                        {i > 0 ? " + " : ""}
                                        {mins}m × {occ}/{v.math_detail.period}{womLabel} = {mins * occ}m
                                      </span>
                                    );
                                  })}
                                  <span> = </span>
                                  <strong className="text-[#1F2937]">
                                    {v.math_detail.total_placed_minutes}m / {v.math_detail.period}
                                  </strong>
                                </div>
                              )}
                            </div>
                            {hasBlock && (
                              <div className="flex items-center gap-1 shrink-0">
                                {sess ? (
                                  <button
                                    type="button"
                                    data-testid={`val-jump-${v.block_id}`}
                                    onClick={() => onJumpToBlock(v.block_id, sess)}
                                    className="text-[10px] text-[#1E3F33] hover:underline px-1.5 py-1"
                                    title="Show on canvas"
                                  >
                                    Show
                                  </button>
                                ) : null}
                                {/* Iter 33d Follow-up 10/14e — Direct
                                    "Remove from schedule" for archived/
                                    graduated student blocks AND for
                                    provider-day-off blocks. Both rows
                                    correspond to blocks that aren't
                                    visually anchored to a canvas slot
                                    (one because the student is gone,
                                    the other because the materializer
                                    drops blocks on off-days), so the
                                    only way to clean them up without
                                    leaving the drawer is a direct
                                    Remove. */}
                                {(v.kind === "archived_student_block" || v.kind === "provider_unavailable_day") && onRemoveBlock ? (
                                  <button
                                    type="button"
                                    data-testid={`val-remove-${v.block_id}`}
                                    onClick={() => onRemoveBlock(v.block_id)}
                                    className="text-[10px] font-medium text-[#B85C5C] bg-[#FDF2F2] hover:bg-[#F7E1E1] rounded-md px-2 py-1"
                                    title="Delete this recurring block"
                                  >
                                    Remove
                                  </button>
                                ) : null}
                                {v.kind !== "archived_student_block" ? (
                                  <button
                                    type="button"
                                    data-testid={`val-find-${v.block_id}`}
                                    onClick={() => toggleAlternatives(v.block_id)}
                                    className="text-[10px] font-medium text-[#1E3F33] bg-[#E8F0EB] hover:bg-[#d5e5d9] rounded-md px-2 py-1"
                                  >
                                    {showAlts ? "Hide" : "Alternatives"}
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </div>

                          {hasBlock && showAlts && (
                            <div
                              data-testid={`val-alts-${v.block_id}`}
                              className="mt-2 ml-0 pl-2.5 border-l-2 border-[#1E3F33]/15 space-y-1"
                            >
                              {cache?.loading ? (
                                <div className="flex items-center gap-1.5 text-[11px] text-[#6B7280] italic py-1">
                                  <ArrowsClockwise size={11} className="animate-spin" />
                                  Checking constraints…
                                </div>
                              ) : cache?.error ? (
                                <p className="text-[11px] text-[#B85C5C] italic">
                                  Could not load suggestions: {cache.error}
                                </p>
                              ) : !cache?.suggestions || cache.suggestions.length === 0 ? (
                                <p className="text-[11px] text-[#6B7280] italic">
                                  {cache?.reason === "no_legal_options"
                                    ? "No legal same-day options found. Try another day or change the applicable rule."
                                    : cache?.reason === "provider_off_this_day"
                                    ? "You're off on this weekday and no other available weekday has a constraint-clear slot for this duration. Update your availability, shorten the block, or remove it."
                                    : cache?.reason === "non_weekday_block"
                                    ? "This block isn't on a weekday. Edit it directly to assign a valid weekday."
                                    : cache?.reason === "non_positive_duration"
                                    ? "Block duration is invalid — fix start/end times."
                                    : "No constraint-clear same-day slots fit this duration. Try a different day, shorten the block, or remove the conflicting rule."}
                                </p>
                              ) : (
                                <>
                                  {cache?.reason === "no_legal_same_day_slot" && (
                                    <p className="text-[10px] text-[#9C6B2E] italic mb-1">
                                      No legal same-day option — every same-day offset overlaps a hard rule. Suggestions below are on other weekdays, closest first.
                                    </p>
                                  )}
                                  {cache?.cross_day && (
                                    <p className="text-[10px] text-[#9C6B2E] italic mb-1">
                                      Same-day not possible — you&apos;re off on this weekday. Suggestions below are on other weekdays, sorted by closest day.
                                    </p>
                                  )}
                                  {cache.suggestions.map((a, j) => {
                                    // Cross-day suggestions carry their own day_of_week.
                                    // Fall back to the row's original day for same-day suggestions.
                                    const altDayLabel =
                                      typeof a.day_of_week === "number"
                                        ? DAYS[a.day_of_week]
                                        : dayLabel || "";
                                    return (
                                    <div
                                      key={j}
                                      className="flex items-center justify-between gap-2 bg-[#F9F8F6] border border-[#E5E3DB] rounded-md px-2.5 py-1.5"
                                    >
                                      <span className="text-[11px] text-[#1F2937] flex items-center gap-1.5">
                                        <ShieldCheck
                                          size={11}
                                          weight="fill"
                                          className="text-[#1E3F33]"
                                          title="Pre-screened against grade rules, attendance windows, student/teacher rules, provider availability, and cross-provider conflicts"
                                        />
                                        <span>
                                          <span className="font-semibold">{altDayLabel}</span>{" "}
                                          {fmtTime(a.start_time)}–{fmtTime(a.end_time)}
                                        </span>
                                        {typeof a.distance_min === "number" && a.distance_min > 0 && !cache?.cross_day && (
                                          <span className="text-[10px] text-[#9B9890]">
                                            ·{" "}
                                            {a.distance_min < 60
                                              ? `${a.distance_min}m`
                                              : `${Math.round((a.distance_min / 60) * 10) / 10}h`}{" "}
                                            away
                                          </span>
                                        )}
                                      </span>
                                      <Button
                                        size="sm"
                                        data-testid={`val-apply-${v.block_id}-${j}`}
                                        onClick={() => {
                                          onMoveBlock(v.block_id, sess, a);
                                          toggleAlternatives(v.block_id);
                                        }}
                                        className="h-6 text-[10px] rounded-md bg-[#1E3F33] hover:bg-[#173127] text-white"
                                      >
                                        Apply
                                      </Button>
                                    </div>
                                    );
                                  })}
                                  {/* FP3-069 follow-up #11 — Builder
                                      suggestion transparency. The same
                                      constraint engine that produced
                                      the validation findings above
                                      pre-screens every suggestion
                                      below; we surface the exact
                                      checklist here so the operator
                                      sees the green ShieldCheck icon
                                      isn't just decoration. */}
                                  <SuggestionTransparency
                                    testid={`val-checks-${v.block_id}`}
                                  />
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}

// FP3-069 follow-up #11 — Suggestion transparency expander.
// Replaces the previous one-line italic footer with a click-to-expand
// checklist so the operator sees exactly what the engine checked.
// The validation engine and the suggestion engine share helpers
// (see backend/routes/schedule_health.py:_load_suggestion_student_constraint_bundle
// landed in FP3-069 follow-up #10) so the list below is the truth, not
// marketing copy.
function SuggestionTransparency({ testid }) {
  const [open, setOpen] = useState(false);
  const CHECKS = [
    "Grade rules for the student's attendance pattern",
    "Attendance window for the student's pattern",
    "Student-specific exclusion windows",
    "Teacher-dependent constraints",
    "Provider availability + day-off",
    "Provider personal blocks (multi-day & cadence-aware)",
    "Cross-provider conflicts for every student in the (group) block",
    "Flex-day and school-day window",
  ];
  return (
    <div className="mt-1 rounded-md border border-[#E5E3DB] bg-[#F9F8F6] px-2 py-1.5" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="text-[10.5px] font-medium text-[#1E3F33] flex items-center gap-1.5">
          <ShieldCheck size={11} weight="fill" className="text-[#1E3F33]" />
          Pre-screened — clear of grade rules, provider blocks, student constraints
        </span>
        {open ? (
          <CaretDown size={10} weight="bold" className="text-[#6B7280]" />
        ) : (
          <CaretRight size={10} weight="bold" className="text-[#6B7280]" />
        )}
      </button>
      {open && (
        <ul
          data-testid={`${testid}-list`}
          className="mt-1.5 pl-4 list-disc text-[10px] text-[#4B5563] space-y-0.5"
        >
          {CHECKS.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
